import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card } from "@/components/ui/card";
import { Star, Calendar, Activity, Clock, Users, X, Pencil, Link as LinkIcon, ListChecks } from "lucide-react";
import { useTaskChecklistWorkspace, isFloatingChecklistTarget } from "@/components/checklists/task-checklist-workspace";
import { format } from "date-fns";
import { sanitizeCategory } from "@/lib/categoryUtils";
import { TaskAttachmentsList } from "@/components/tasks/task-attachments";
import DocumentUploadButton from "@/components/document-upload-button";

export function TaskViewDialog({ viewingTask, setViewingTask, setEditingTask, isOverdue, subTasksByTaskId, getLinkedItemName, onSubtaskStar: handleInlineSubtaskStar }: any) {
  // Tasks shared with the current user as view-only must not expose editable sub-task controls.
  const subtasksReadOnly = !!viewingTask?.isShared && viewingTask?.sharedPermission !== "edit";
  const { openChecklist, isChecklistOpen } = useTaskChecklistWorkspace();
  return (
      <Dialog modal={!isChecklistOpen} open={!!viewingTask} onOpenChange={(isOpen) => {
        if (!isOpen) setViewingTask(null);
      }}>
        <DialogContent
          className="w-screen h-screen m-0 rounded-none overflow-y-auto"
          data-testid="dialog-view-task"
          onInteractOutside={(e) => { if (isFloatingChecklistTarget(e.target) || isChecklistOpen) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {viewingTask?.title}
              {viewingTask?.isTopThree && (
                <Star className="h-5 w-5" fill="#f59e0b" style={{ color: "#f59e0b" }} />
              )}
            </DialogTitle>
            <DialogDescription>View all task details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Description */}
            {viewingTask?.description && (
              <div>
                <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Description</h4>
                <p className="text-foreground whitespace-pre-wrap">{viewingTask.description}</p>
              </div>
            )}

            {viewingTask?.checklistId && (
              <div>
                <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Checklist</h4>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openChecklist(viewingTask.checklistId)}
                  aria-label="Open attached checklist in a floating window"
                  data-testid="button-open-task-checklist"
                >
                  <ListChecks className="h-4 w-4 mr-2 text-primary" />
                  Open checklist
                </Button>
              </div>
            )}

            {/* Category */}
            <div>
              <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Category</h4>
              <Badge variant="outline" data-testid="view-task-category">
                {sanitizeCategory(viewingTask?.category) || "Uncategorized"}
              </Badge>
            </div>

            {/* Priority */}
            <div>
              <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Priority</h4>
              <Badge 
                variant={
                  viewingTask?.priority === "high" ? "destructive" :
                  viewingTask?.priority === "medium" ? "default" :
                  "secondary"
                }
                data-testid="view-task-priority"
              >
                {viewingTask?.priority ? viewingTask.priority.charAt(0).toUpperCase() + viewingTask.priority.slice(1) : "Medium"}
              </Badge>
            </div>

            {/* Status */}
            <div>
              <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Status</h4>
              <Badge variant={viewingTask?.completed ? "default" : "secondary"} data-testid="view-task-status">
                {viewingTask?.completed ? "Completed" : "Active"}
              </Badge>
            </div>

            {/* Tags */}
            {viewingTask?.tags && viewingTask.tags.length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {viewingTask.tags.map((tag: any) => (
                    <Badge key={tag} variant="outline" data-testid={`view-task-tag-${tag}`}>
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Assigned To */}
            <div>
              <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Assigned To</h4>
              <p className="text-foreground" data-testid="view-task-assigned-to">
                {viewingTask?.assignedTo === "Others" && viewingTask?.assignedToOther
                  ? `${viewingTask.assignedToOther}`
                  : viewingTask?.assignedTo || "Me"}
              </p>
            </div>

            {/* Due Date */}
            {viewingTask?.dueDate && (
              <div>
                <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Due Date</h4>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <p className={`text-foreground ${isOverdue(viewingTask) ? "text-red-600 font-medium" : ""}`} data-testid="view-task-due-date">
                    {format(new Date(viewingTask.dueDate), "MMMM d, yyyy")}
                    {isOverdue(viewingTask) && <span className="ml-2 text-sm">(Overdue)</span>}
                  </p>
                </div>
              </div>
            )}

            {/* Linked To */}
            {getLinkedItemName(viewingTask!) && (
              <div>
                <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Linked To</h4>
                <div className="flex items-center gap-2">
                  <LinkIcon className="h-4 w-4 text-muted-foreground" />
                  <p className="text-foreground" data-testid="view-task-linked-to">
                    {getLinkedItemName(viewingTask!)?.type}: <span className="font-medium">{getLinkedItemName(viewingTask!)?.name}</span>
                  </p>
                </div>
              </div>
            )}

            {/* Top Three Status */}
            {viewingTask?.isTopThree && (
              <div>
                <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Top Three Task</h4>
                <Badge variant="default" className="bg-amber-500 hover:bg-amber-600" data-testid="view-task-top-three">
                  <Star className="h-3 w-3 mr-1" fill="white" />
                  Priority Task
                </Badge>
              </div>
            )}

            {/* Created Date */}
            {viewingTask?.createdAt && (
              <div>
                <h4 className="font-semibold mb-2 text-sm text-muted-foreground">Created</h4>
                <p className="text-sm text-muted-foreground" data-testid="view-task-created-at">
                  {format(new Date(viewingTask.createdAt), "MMMM d, yyyy 'at' h:mm a")}
                </p>
              </div>
            )}

            {/* Sub-tasks */}
            {viewingTask && (() => {
              const taskSubTasks = subTasksByTaskId.get(viewingTask.id) || [];
              if (taskSubTasks.length > 0) {
                const completed = taskSubTasks.filter((st: any) => st.completed).length;
                return (
                  <div>
                    <h4 className="font-semibold mb-2 text-sm text-muted-foreground">
                      Sub-tasks ({completed}/{taskSubTasks.length} completed)
                    </h4>
                    <div className="space-y-2" data-testid="view-task-subtasks">
                      {taskSubTasks.map((subTask: any) => (
                        <div 
                          key={subTask.id} 
                          className="flex items-start gap-2 p-2 sm:p-3 border rounded bg-card"
                          data-testid={`view-subtask-${subTask.id}`}
                        >
                          <div className="flex items-center pt-0.5">
                            <input
                              type="checkbox"
                              checked={subTask.completed}
                              readOnly
                              disabled
                              className="w-4 h-4 rounded border-gray-300"
                              data-testid={`view-checkbox-subtask-${subTask.id}`}
                            />
                          </div>
                          <div className="flex-1 min-w-0 space-y-1">
                            <p 
                              className={`text-sm break-words ${subTask.completed ? 'line-through text-muted-foreground' : 'text-foreground'}`}
                              data-testid={`view-subtask-text-${subTask.id}`}
                            >
                              {subTask.text}
                            </p>
                            {subTask.dueDate && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Calendar className="h-3 w-3" />
                                <span data-testid={`view-subtask-date-${subTask.id}`}>
                                  Due: {format(new Date(subTask.dueDate), "MMM d, yyyy")}
                                </span>
                              </div>
                            )}
                          </div>
                          <button
                            disabled={subtasksReadOnly}
                            onClick={() => { if (!subtasksReadOnly) handleInlineSubtaskStar?.(subTask.id); }}
                            className={`flex-shrink-0 p-1 rounded transition-colors ${subtasksReadOnly ? "cursor-not-allowed text-muted-foreground/50" : `hover:bg-muted ${subTask.isStarred ? "text-amber-500" : "text-muted-foreground hover:text-amber-500"}`}`}
                            title={subtasksReadOnly ? "View-only shared task — you can't edit sub-tasks" : subTask.isStarred ? "Remove star" : "Star subtask"}
                            data-testid={`button-star-subtask-view-${subTask.id}`}
                          >
                            <Star className="h-4 w-4" style={subTask.isStarred ? { fill: "#f59e0b" } : {}} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Document Attachments */}
            {viewingTask && (() => {
              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-sm text-muted-foreground">Documents</h4>
                    <DocumentUploadButton
                      entityType="task"
                      entityId={viewingTask.id}
                    />
                  </div>
                  <TaskAttachmentsList taskId={viewingTask.id} />
                </div>
              );
            })()}
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setViewingTask(null)}
              data-testid="button-close-view-task"
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setEditingTask(viewingTask);
                setViewingTask(null);
              }}
              data-testid="button-edit-from-view"
            >
              <Pencil className="h-4 w-4 mr-2" />
              Edit Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
  );
}
